import { useCallback, useEffect } from 'react';

import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS } from 'react-joyride';
import { getTourGuide, updateTourGuide } from '@api/user';
import useMediaQuery from '@mui/material/useMediaQuery';
import { handlerDrawerOpen } from '@api/menu.ts';
import { useTheme } from '@mui/material/styles';

const joyrideSteps = [
  {
    target: '#step0',
    content: (
      <div>
        <strong>Welcome to Zlayt AI! 🎉</strong>
        <p style={{ marginTop: '8px', fontSize: '14px' }}>
          Your AI-powered legal document generation platform. Let's take a quick
          tour to get you started.
        </p>
      </div>
    ),
  },
  {
    target: '#create-case-card',
    content: (
      <div>
        <strong>Create a New Case</strong>
        <p style={{ marginTop: '8px', fontSize: '14px' }}>
          Start here to create a new case. You'll enter client information, case
          details, and upload relevant documents. Each case serves as a
          container for all related documents.
        </p>
      </div>
    ),
  },
  {
    target: '#existing-cases-card',
    content: (
      <div>
        <strong>Work on Existing Cases</strong>
        <p style={{ marginTop: '8px', fontSize: '14px' }}>
          Access your existing cases to view, edit, and manage documents. All
          your cases are organized here for easy access.
        </p>
      </div>
    ),
  },
  {
    target: 'body',
    placement: 'center' as const,
    styles: {
      options: { width: 560 },
    },
    content: (
      <div style={{ textAlign: 'left' }}>
        <strong>🚀 Ready to Get Started?</strong>
        <p style={{ marginTop: '8px', fontSize: '14px' }}>
          Click "Create a New Case" to begin. We'll guide you through the
          process of:
        </p>
        <ul
          style={{
            marginTop: '8px',
            fontSize: '14px',
            paddingLeft: '20px',
            textAlign: 'left',
          }}
        >
          <li>Entering client information</li>
          <li>Adding case details (incident type, date, location)</li>
          <li>Uploading relevant documents</li>
          <li>Generating AI-powered legal documents</li>
        </ul>
        <p style={{ marginTop: '8px', fontSize: '14px' }}>
          You can always access this tour again from the Help menu.
        </p>
      </div>
    ),
  },
];

interface TourGuideProps {
  isTourRunning: boolean;
  stepIndex: number;
  setIsTourRunning: (isTourRunning: boolean) => void;
  setStepIndex: (stepsIndex: number) => void;
  setShowTourModal: (showTourModal: boolean) => void;
}

function TourGuide(props: TourGuideProps) {
  const {
    isTourRunning,
    stepIndex,
    setIsTourRunning,
    setStepIndex,
    setShowTourModal,
  } = props;

  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const hasSeenTour = useCallback(async () => {
    const data = await getTourGuide();
    if (data.has_seen_tour) setShowTourModal(false);
    else setShowTourModal(true);
  }, [setShowTourModal]);

  useEffect(() => {
    if (!isSmallScreen || !isTourRunning) return;

    if (stepIndex === 1) {
      handlerDrawerOpen(true);
    } else if (stepIndex === 0) {
      handlerDrawerOpen(false);
    }
  }, [stepIndex, isSmallScreen, isTourRunning]);

  const handleJoyrideCallback = async (data: CallBackProps) => {
    const { status, action, index, type } = data;

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setIsTourRunning(false);
      updateTourGuide();

      if (isSmallScreen) {
        handlerDrawerOpen(false);
      }
      return;
    }

    if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
      setStepIndex(index + 1);
    }

    if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
      setStepIndex(index - 1);
    }
  };

  useEffect(() => {
    hasSeenTour();
  }, [hasSeenTour]);

  return (
    <Joyride
      steps={joyrideSteps}
      run={isTourRunning}
      stepIndex={stepIndex}
      continuous
      showSkipButton
      showProgress
      callback={handleJoyrideCallback}
      locale={{
        last: 'Finish',
      }}
      styles={{
        options: { primaryColor: '#2479BF', zIndex: 10000 },
      }}
    />
  );
}

export default TourGuide;
